import React, { useState } from 'react';
import { DialogTitle, DialogFooter, DialogContent, DialogButton } from '../AlmeDialog/ModalDialog';
import { dateAddDays, formatdate, getCurrentDate, getEndDate, getStartDate, parseDate } from '../AlmeUtilities/dateutils';
import { PromptText } from './controls/control.styles';
import DatePickerControl from './controls/DatePickerControl';
import DropdownControl from './controls/DropdownControl';
import RadioButtonControl from './controls/RadioButtonControl';
import TextboxControl, { MatchFail } from './controls/TextboxControl';
import styled from 'styled-components';
const DateInputType = "Date";
const DropdownInputType = "Dropdown";
const TextInputType = "Text";
const Required = "true";
const RequireField = "The field is required and must not be blank";
const RadioInputType = "Radio";
function getErrorMsg(inputValue) {
    switch (inputValue) {
        case "FirstName":
            return `${RequireField}, and this field must be 22 characters or less.`;
        case "LastName":
            return `${RequireField}, and this field must be 22 characters or less.`;
        case "ZipCode":
            return `${RequireField}, and zip code should be 5 or 9 digit code.`;
        case "DOB":
            return `${RequireField}, and must follow the MM/DD/YYYY format.`;
        case "LastFourSSN":
            return `${RequireField}, and it must be the last 4 digits of your SSN.`;
        default:
            return `${RequireField}`;
    }
}
function getValue(dataInput, dataInputs) {
    if (dataInput.type === DateInputType) {
        const dateSpec = dataInput;
        let defaultDate = parseDate(dataInput.value);
        if (!defaultDate && dateSpec.parentId) {
            const parentDependentDays = dataInputs.find(x => x.id === dateSpec.parentId).dependentDateDays;
            if (parentDependentDays) {
                defaultDate = dateAddDays(getCurrentDate(), parentDependentDays);
            }
        }
        // When default date value is not given, assign current date as default value only when we show calendar
        // For certain DOB kind of dates do not show calendar and do not assign default value when not given
        if (!defaultDate && !dateSpec.noCalendar) {
            defaultDate = getCurrentDate();
        }
        return defaultDate;
    }
    else if (dataInput.type === DropdownInputType) {
        return dataInput.selected;
    }
    else {
        return dataInput.value;
    }
}
function initializeInputValues(metadata) {
    return metadata.dataInputs.map(input => {
        const isRequired = input.required === Required;
        const value = getValue(input, metadata.dataInputs);
        let matchId;
        let matchFailedText;
        if (input.match) {
            const textInput = input;
            const matchInputSpec = metadata.dataInputs.find(i => i.id !== input.id && i.match === textInput.match);
            matchId = matchInputSpec.id;
            matchFailedText = textInput.matchfailedtext || matchInputSpec.matchfailedtext;
        }
        return {
            id: input.id,
            isvalid: !!(isRequired && value) || !isRequired,
            value: typeof value === "number" ? String(value) : value,
            matchId,
            matchFailedText,
        };
    });
}
const Container = styled.div `
  padding: 1rem;
`;
const DataCollectionPane = (props) => {
    const [inputValues, setInputValues] = useState(() => initializeInputValues(props.metadata.metadata));
    const resetFocusValues = (values) => {
        for (const item of values) {
            item.shouldFocus = false;
        }
    };
    const setFocus = (newInputValues) => {
        for (const item of props.metadata.metadata.dataInputs) {
            const errorControl = newInputValues.find(x => x.id === item.id);
            if (errorControl && !(errorControl === null || errorControl === void 0 ? void 0 : errorControl.isvalid)) {
                errorControl.shouldFocus = true;
                break;
            }
        }
    };
    const onChange = (inputChangeParam) => {
        let values = [...inputValues];
        const index = values.findIndex(val => val.id === inputChangeParam.id);
        const inputValue = Object.assign(Object.assign({}, values.splice(index, 1)[0]), inputChangeParam);
        const changedInputValues = [inputValue];
        if (inputValue.isvalid) {
            inputValue.error = undefined;
        }
        else {
            inputValue.error = inputValue.error || getErrorMsg(inputValue.id);
        }
        if (inputValue.matchId) {
            const matchInputIndex = values.findIndex(x => x.id === inputValue.matchId);
            const matchInput = Object.assign({}, values.splice(matchInputIndex, 1)[0]);
            if (inputValue.error === MatchFail) {
                matchInput.error = matchInput.matchFailedText;
                matchInput.isvalid = false;
                inputValue.error = inputValue.matchFailedText;
            }
            else if (inputValue.value === matchInput.value && !inputValue.error) {
                matchInput.error = undefined;
                matchInput.isvalid = true;
                inputValue.error = undefined;
            }
            changedInputValues.push(matchInput);
        }
        values = [...values, ...changedInputValues];
        resetFocusValues(values);
        setInputValues(values);
    };
    function isValidOptionSelected(inputValuesObj) {
        if (inputValuesObj[0].id === "ClosureReason")
            return (inputValuesObj[0].id === "ClosureReason" && typeof inputValuesObj[0].value === "string");
        else
            return true;
    }
    const handleFormSubmit = () => {
        const values = {};
        let isValid = true;
        const newInputValues = [...inputValues];
        const activeButton = document.activeElement;
        activeButton.disabled = true;
        isValid = isValidOptionSelected(inputValues);
        inputValues.forEach((inputVal, index) => {
            if (inputVal.isvalid) {
                values[inputVal.id] = inputVal.value instanceof Date ? formatdate(inputVal.value) : inputVal.value;
            }
            else {
                isValid = false;
                if (!inputVal.error) {
                    const newVal = Object.assign(Object.assign({}, inputVal), { error: getErrorMsg(inputVal.id) });
                    newInputValues[index] = newVal;
                }
            }
        });
        if (isValid) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            props.onSubmit(values, true);
        }
        else {
            setFocus(newInputValues);
            setInputValues(newInputValues);
        }
        if (!isValid) {
            activeButton.disabled = false;
        }
    };
    const handleFormCancel = () => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        props.onSubmit({}, false);
    };
    return (React.createElement(Container, null,
        React.createElement(DialogTitle, { id: 'data-collection-dialog-title' }, props.metadata.header),
        React.createElement(DialogContent, null,
            React.createElement(PromptText, { id: 'data-collection-dialog-prompt-text' }, props.metadata.prompt),
            props.metadata.metadata.dataInputs.map((controlSpec, index) => {
                var _a, _b, _c, _d;
                const inputValue = inputValues.find(v => v.id === controlSpec.id);
                const errorMessage = inputValue === null || inputValue === void 0 ? void 0 : inputValue.error;
                const focus = inputValue === null || inputValue === void 0 ? void 0 : inputValue.shouldFocus;
                if (controlSpec.type === DateInputType) {
                    const dateSpec = controlSpec;
                    const hasParent = !!(dateSpec.parentId && dateSpec.parentId.length >= 1);
                    const hasChild = !!(dateSpec.dependentDateId && dateSpec.dependentDateId.length >= 1);
                    const parentDate = dateSpec.parentId && ((_a = inputValues.find(x => x.id === dateSpec.parentId)) === null || _a === void 0 ? void 0 : _a.value);
                    const childDate = dateSpec.dependentDateId && ((_b = inputValues.find(x => x.id === dateSpec.dependentDateId)) === null || _b === void 0 ? void 0 : _b.value);
                    const parentDependentDays = dateSpec.parentId && ((_c = props.metadata.metadata.dataInputs.find(x => x.id === dateSpec.parentId)) === null || _c === void 0 ? void 0 : _c.dependentDateDays);
                    return (React.createElement(DatePickerControl, { key: controlSpec.id, tabIndex: 0, inputMetadata: dateSpec, onChange: onChange, startDate: getStartDate(+dateSpec.minDays, hasParent, parentDate), endDate: getEndDate(+dateSpec.maxDays, parentDate, parentDependentDays, hasChild, childDate), value: inputValue === null || inputValue === void 0 ? void 0 : inputValue.value, error: errorMessage, isFirstOverlay: index === 0, shouldFocus: focus, showCalendar: !dateSpec.noCalendar }));
                }
                else if (controlSpec.type === DropdownInputType) {
                    const dropdownSpec = controlSpec;
                    return (React.createElement(DropdownControl, { key: controlSpec.id, tabIndex: 0, inputMetadata: dropdownSpec, onChange: onChange, shouldFocus: focus, value: inputValue === null || inputValue === void 0 ? void 0 : inputValue.value, error: errorMessage }));
                }
                else if (controlSpec.type === RadioInputType) {
                    const radiobuttonSpec = controlSpec;
                    return (React.createElement(RadioButtonControl, { key: controlSpec.id, tabIndex: 0, inputMetadata: radiobuttonSpec, onChange: onChange, value: inputValue === null || inputValue === void 0 ? void 0 : inputValue.value, shouldFocus: focus, error: errorMessage }));
                }
                else if (controlSpec.type === TextInputType) {
                    const textboxSpec = controlSpec;
                    const matchValue = (inputValue === null || inputValue === void 0 ? void 0 : inputValue.matchId) && ((_d = inputValues.find(x => x.id === inputValue.matchId)) === null || _d === void 0 ? void 0 : _d.value);
                    return (React.createElement(TextboxControl, { key: controlSpec.id, tabIndex: 0, inputMetadata: textboxSpec, onChange: onChange, value: inputValue === null || inputValue === void 0 ? void 0 : inputValue.value, error: errorMessage, matchValue: matchValue, valid: inputValue === null || inputValue === void 0 ? void 0 : inputValue.isvalid, shouldFocus: focus, autoComplete: inputValue === null || inputValue === void 0 ? void 0 : inputValue.id }));
                }
                else {
                    console.error('Unknown control error', controlSpec);
                    return null;
                }
            })),
        React.createElement(DialogFooter, null,
            React.createElement(DialogButton, { id: 'data-collection-pane-yes-button', buttonType: 'primary', onClick: handleFormSubmit }, props.metadata.metadata.buttonText),
            React.createElement(DialogButton, { id: 'data-collection-pane-no-button', buttonType: 'ghost', onClick: handleFormCancel }, props.metadata.metadata.cancelText))));
};
export default DataCollectionPane;
